import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useCity } from "@/context/CityContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Store, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function BecomeVendor() {
  const { city, cities } = useCity();
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    business_name: "", owner_name: "", phone: "", email: "", password: "", city: city || "Kundapura",
    categories: [], services_offered: "", description: "",
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });

  const toggleCat = (slug) =>
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(slug)
        ? f.categories.filter((c) => c !== slug)
        : [...f.categories, slug],
    }));

  const submit = async () => {
    if (!form.business_name || !form.owner_name || !form.phone || !form.email || !form.password || !form.city || !form.services_offered) {
      toast.error("Please fill all required fields (including a login password)");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/vendor-applications", form);
      setDone(true);
    } catch {
      toast.error("Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="px-4 py-24 text-center max-w-md mx-auto" data-testid="vendor-app-success">
        <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-500" />
        <h1 className="font-display font-extrabold text-3xl mt-4">Application received!</h1>
        <p className="text-muted-foreground mt-2">
          Thanks for your interest in partnering with Book My Event. Our team will review your
          details and get in touch within 2 business days.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-8 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-11 w-11 rounded-xl bg-primary/10 grid place-items-center">
          <Store className="h-5 w-5 text-primary" />
        </div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight">List your business</h1>
      </div>
      <p className="text-muted-foreground mb-6">
        Reach thousands of customers planning weddings and celebrations. Tell us about your services.
        Already a partner? <a href="/vendor/login" data-testid="vendor-login-link" className="text-primary font-semibold">Log in to your portal</a>.
      </p>

      <div className="grid gap-3">
        <Input data-testid="v-business" placeholder="Business name *" value={form.business_name}
          onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
        <div className="grid md:grid-cols-2 gap-3">
          <Input data-testid="v-owner" placeholder="Owner name *" value={form.owner_name}
            onChange={(e) => setForm({ ...form, owner_name: e.target.value })} />
          <select
            data-testid="v-city"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="h-10 rounded-md border border-input bg-card px-3 text-sm"
          >
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Input data-testid="v-phone" placeholder="Phone *" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input data-testid="v-email" placeholder="Email *" type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <Input data-testid="v-password" placeholder="Create a login password * (for your vendor portal)"
          type="password" value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })} />

        <div>
          <p className="text-sm font-semibold mb-2">Occasions you serve</p>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c.id}
                data-testid={`v-cat-${c.slug}`}
                onClick={() => toggleCat(c.slug)}
                className={`px-3 h-9 rounded-full text-sm font-semibold border transition-colors ${
                  form.categories.includes(c.slug)
                    ? "bg-primary text-white border-primary"
                    : "border-stone-300 text-muted-foreground"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <Textarea data-testid="v-services" placeholder="Services offered * (e.g. Stage Decoration, DJ, Catering)"
          value={form.services_offered}
          onChange={(e) => setForm({ ...form, services_offered: e.target.value })} />
        <Textarea data-testid="v-desc" placeholder="Tell us about your business"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })} />

        <button
          data-testid="submit-vendor-app"
          onClick={submit}
          disabled={submitting}
          className="h-12 bg-primary text-white rounded-full font-semibold disabled:opacity-60 hover:bg-primary/90 transition-colors"
        >
          {submitting ? "Submitting..." : "Submit application"}
        </button>
      </div>
    </div>
  );
}
